import React, { useCallback, useState, useEffect } from 'react';
import enTranslations from '@shopify/polaris/locales/en.json';
import { path, propOr, pathOr, isEmpty, includes, equals, sortBy, compose, reverse, prop, isNil } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_EMAIL_TEMPLATES } from '../apollo/queries';
import moment from 'moment';
import {
  Page,
  Layout,
  Button,
  Card,
  EmptyState,
  AppProvider,
  Frame,
  Badge,
  ResourceList,
  Filters,
  ChoiceList,
} from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import Footer from './Footer';
import { emptyStateImage } from '../../utils/imagesUrl';

const img = 'https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg';

const EmailTemplateList = (props) => {
  let { history, shopifyToken } = props;

  const [isLoading, setLoading] = useState(false);
  const [queryValue, setQueryValue] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [sortValue, setSortValue] = useState('RECENTLY_UPDATED');
  const [rows, setRows] = useState([]);
  const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

  const { loading, data, refetch } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: { shopifyToken },
  });

  const emailList = propOr([], 'emailTemplates')(data);
  // const jobs = propOr([], 'jobs')(jobList);
  // const allAccess = propOr(null, 'allAccess')(jobList);
  const changeRoute = path(['history', 'push'])(props);
  // const allJobs   = []

  useEffect(() => {
    refetch();
    setRows(emailList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailList]);

  const handleStatusFilter = (value) => {
    setSelectedStatus(value);

    if (isNilOrEmpty(queryValue) && isNilOrEmpty(value)) {
      return setRows(emailList);
    }

    setRows(
      emailList.filter((item) => {
        const { status, title } = { ...item };
        if (!!queryValue) {
          return includes(`${queryValue}`.toLowerCase(), `${title}`.toLowerCase()) && includes(status, value);
        } else {
          return includes(status, value);
        }
      })
    );
  };

  const handleFiltersQueryChange = (value) => {
    setQueryValue(value);

    if (isNilOrEmpty(value)) {
      return setRows(emailList);
    }

    setRows(
      emailList.filter((item) => {
        const { title } = { ...item };
        return includes(`${value}`.toLowerCase(), `${title}`.toLowerCase());
      })
    );
  };

  const handleQueryValueRemove = () => {
    setQueryValue();
    setRows(emailList);
  };

  if (emailList.length === 0 && !loading) {
    return (
      <AppProvider>
        <Layout>
          <EmptyState
            heading='Manage your job postings'
            action={{ content: 'Open Job', onAction: () => changeRoute('/set-job') }}
            secondaryAction={{ content: 'Learn more', url: 'https://help.shopify.com' }}
            image={img}>
            <p>Open jobs and start receiving applications from job seekers.</p>
          </EmptyState>
        </Layout>
      </AppProvider>
    );
  }

  const onRenderListItems = ({ title, subject, updatedAt, status, id }) => {
    const onEditTemplate = () => changeRoute(`/edit-email-template/${id}`);
    return (
      <ResourceList.Item id={id} accessibilityLabel={`View details for ${title}`}>
        <table>
          <tbody>
            <tr>
              <th style={{ width: '30%' }} onClick={onEditTemplate}>
                <b className='capitalize'>{title}</b>
                <div>
                  <span style={{ fontWeight: '300', fontSize: '12px' }}> Updated {moment(updatedAt).format('MMMM D, YYYY')}</span>
                </div>
              </th>
              <th style={{ width: '12%' }} onClick={onEditTemplate}>
                <Badge status={status === 'active' ? 'success' : 'default'}>
                  <span className={'capitalize'}>{status}</span>
                </Badge>
              </th>
              <th className='text-left' style={{ width: '50%' }} onClick={onEditTemplate}>
                {subject}
              </th>
              <th style={{ width: '12%' }}>
                <Button onClick={onEditTemplate} fullWidth size='small' ary>
                  Edit
                </Button>
              </th>
            </tr>
          </tbody>
        </table>
      </ResourceList.Item>
    );
  };

  const listSortOptions = [
    { label: 'Recently updated', value: 'RECENTLY_UPDATED' },
    { label: 'Newest', value: 'CREATED_DES' },
    { label: 'Oldest', value: 'CREATED_ASC' },
    { label: 'Title A - Z', value: 'TITLE_ASC' },
    { label: 'Title Z - A', value: 'TITLE_DES' },
  ];

  const onListSortChange = (selected) => {
    setSortValue(selected);

    if (equals(selected, 'RECENTLY_UPDATED')) {
      compose(setRows, reverse, sortBy(prop('updatedAt')))(rows);
    }

    if (equals(selected, 'CREATED_DES')) {
      compose(setRows, reverse, sortBy(prop('createdAt')))(rows);
    }

    if (equals(selected, 'CREATED_ASC')) {
      compose(setRows, sortBy(prop('createdAt')))(rows);
    }

    if (equals(selected, 'TITLE_ASC')) {
      compose(setRows, sortBy(prop('title')))(rows);
    }

    if (equals(selected, 'TITLE_DES')) {
      compose(setRows, reverse, sortBy(prop('title')))(rows);
    }
  };

  const listResourceName = { singular: 'template', plural: 'templates' };

  const disambiguateLabel = (key, value) => {
    switch (key) {
      case 'status':
        const mappedValue = value
          .map((el) => {
            return `${el.charAt(0).toUpperCase()}${el.slice(1)}`;
          })
          .join(', ');
        return `By status: ${mappedValue}`;
      default:
        return value;
    }
  };

  const appliedFilters = !isNilOrEmpty(selectedStatus)
    ? [
        {
          key: 'status',
          label: disambiguateLabel('status', selectedStatus),
          onRemove: () => handleStatusFilter([]),
        },
      ]
    : [];

  return (
    <AppProvider i18n={enTranslations}>
      <Frame>
        <TitleBar title='Email Templates' breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]} />
        <Page
          title='Email Templates'
        breadcrumbs={[{ content: 'Settings', onAction: () => { history.push('/settings'); } }]}
          primaryAction={{ content: 'Create email template', onAction: () => changeRoute('/create-email-template') }}>
          <Card>
            <div className='position-relative'>
              <ResourceList
                items={rows}
                renderItem={onRenderListItems}
                resourceName={listResourceName}
                loading={loading}
                sortValue={sortValue}
                sortOptions={listSortOptions}
                onSortChange={onListSortChange}
                filterControl={
                  <Filters
                    filters={[
                      {
                        key: 'status',
                        label: 'Status',
                        filter: (
                          <ChoiceList
                            title='Status'
                            titleHidden
                            choices={[
                              { label: 'Active', value: 'active' },
                              { label: 'Archived', value: 'archived' },
                            ]}
                            selected={selectedStatus || []}
                            onChange={handleStatusFilter}
                            allowMultiple
                          />
                        ),
                        shortcut: true,
                      },
                    ]}
                    appliedFilters={appliedFilters}
                    queryValue={queryValue}
                    onQueryChange={handleFiltersQueryChange}
                    onQueryClear={handleQueryValueRemove}
                    emptySearchState={
                      <EmptyState heading='No job description templates found' image={emptyStateImage}>
                        <p>Try changing the filter or search term.</p>
                      </EmptyState>
                    }
                    emptyState={
                      <EmptyState heading='No job description templates found' image={emptyStateImage}>
                        <p>Create your first email template.</p>
                      </EmptyState>
                    }
                  />
                }
              />
            </div>
          </Card>
          <br />
        </Page>
        <Footer
          link='https://help.shophire.co/article/32-how-to-customize-the-application-confirmation-email'
          name='Email templates'
        />
      </Frame>
    </AppProvider>
  );

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === '' || value == null;
    }
  }
};

export default EmailTemplateList;
